import { useCallback } from 'react';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { omit, prop } from 'ramda';
import { useDispatch, useSelector } from 'react-redux';

import Dialog from 'app/components/MaterialDialog';
import Version from 'components/atoms/Version';
import { EMPTY_VALUES } from 'constants/general';
import PAGES from 'constants/pages';
import dialog from 'features/common/dialog';
import { resetErrorsByPage } from 'features/common/submitFormDialog/slice';
import { dialogs } from 'features/views/settings/marketPlaceAgencies/editAgencyModal/constants';
import { EDIT_AGENCY_MODAL_FORM_NAME } from 'features/views/settings/marketPlaceAgencies/editAgencyModal/form';
import {
	getAgencyManagersAndSubordinates,
	getOriginalValues,
	getPristineStates,
} from 'features/views/settings/marketPlaceAgencies/selectors';
import {
	resetAgencyManagersAndSubordinates,
	saveAgencyManagersAndSubordinates,
	saveOriginalValues,
} from 'features/views/settings/marketPlaceAgencies/slice';
import { joinString } from 'utils/string';

import EditAgencyModalBody from './EditAgencyModalBody';

export const EditAgencyModal = NiceModal.create(({ agency }) => {
	const dispatch = useDispatch();
	const modal = useModal();
	const { agencyManagersAndSubordinates: originalAgencyManagersAndSubordinates } = useSelector(getOriginalValues);
	const pristineStates = useSelector(getPristineStates);
	const agencyManagersAndSubordinates = useSelector(getAgencyManagersAndSubordinates);

	const onExit = useCallback(() => {
		dispatch(dialog.actions.close(EDIT_AGENCY_MODAL_FORM_NAME));
		dispatch(resetErrorsByPage({ page: EDIT_AGENCY_MODAL_FORM_NAME }));
		dispatch(dialog.actions.close(dialogs.NON_UNIQUE_OR_NON_EXISTANT_USER));

		const isAgencyPristine = prop(agency?.areaId, pristineStates);
		if (isAgencyPristine) {
			// returning empty state, because there have not been any changes yet - agency is still pristine
			const updated = omit([agency?.areaId], agencyManagersAndSubordinates);
			dispatch(resetAgencyManagersAndSubordinates());
			dispatch(saveAgencyManagersAndSubordinates(updated));
		} else {
			// returning the "most recent" original data
			dispatch(saveAgencyManagersAndSubordinates(originalAgencyManagersAndSubordinates));
		}

		// deleting temporary saved original agencyManagersAndSubordinates values
		dispatch(saveOriginalValues({ agencyManagersAndSubordinates: EMPTY_VALUES.OBJECT }));
	}, [
		dispatch,
		originalAgencyManagersAndSubordinates,
		agencyManagersAndSubordinates,
		agency?.areaId,
		pristineStates,
	]);

	return (
		<Dialog
			modal={modal}
			title={joinString(' - ', [agency?.areaId, agency?.name])}
			content={<EditAgencyModalBody agency={agency} />}
			maxWidth="xl"
			onExit={onExit}
			versionDialogContentProps={{ sx: { overflow: 'unset' } }}
			version={
				<Version
					screenId={PAGES.settings.editAgencyModal.id}
					screenName={PAGES.settings.editAgencyModal.name}
				/>
			}
		/>
	);
});

export default EditAgencyModal;
